import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { getAnchorName } from 'site-modules/shared/utils/upper-funnel/type-rankings';
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import { RANKING_DATA } from 'site-modules/upper-funnel/constants/ranking-data';
import { VehicleSubtypeRankingsList } from 'site-modules/upper-funnel/components/type/vehicle-subtype-rankings-list/vehicle-subtype-rankings-list';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { FilmstripTypeRankingFeatured } from 'site-modules/shared/components/native-ad/filmstrip-type-ranking/filmstrip-type-ranking-featured';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { TYPES_FOR_PRELOAD_ABOVE_FOLD } from 'site-modules/upper-funnel/constants/types-for-preload-above-fold';
import { REPLACE_TYPES } from 'site-modules/shared/constants/upper-funnel/type-rankings';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

import './vehicle-type-rankings-list.scss';

const winnerFlag = (
  <svg xmlns="http://www.w3.org/2000/svg" className="pos-a" style={{ width: '0', height: '0', visibility: 'hidden' }}>
    <defs>
      <linearGradient
        id="winner-grad"
        x1={66.413}
        x2={2.114}
        y1={106.987}
        y2={-32.293}
        gradientTransform="matrix(1 0 0 -1 0 88)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset={0} style={{ stopColor: '#c2aa4c' }} />
        <stop offset=".01" style={{ stopColor: '#c2aa4c' }} />
        <stop offset=".3" style={{ stopColor: '#A4891E' }} />
        <stop offset={1} style={{ stopColor: '#facc15' }} />
      </linearGradient>
    </defs>
    <symbol id="winner-flag" viewBox="0 0 76 82" width={76}>
      <path d="M0 4c0-2.2 1.8-4 4-4h68c2.2 0 4 1.8 4 4v78L38 63.4 0 82V4z" fill="url('#winner-grad')" />
    </symbol>
  </svg>
);

export function VehicleTypeRankingsListUI({
  vehicleEditorialRankings,
  children,
  type,
  selectedCars,
  onSelectCar,
  isMobile,
  isLazySubtypesFFDisabled,
  isSubtypePage,
  isSubtypeExpansionPage,
  trkParam,
  subtypeAdPlacementIndices,
  isReplaceImage,
}) {
  const renderFilmstripTypeRankingAd = (pos = '0') => (
    <FilmstripTypeRankingFeatured
      mobile={isMobile}
      wrapperClass="mt-1 mb-3_5"
      cardClass="ranking-vehicle-card is-large-card"
      position={pos}
    />
  );
  const renderFilmstripAdUnits = (
    <MinHeightWrapper componentType="SPONSORED_CARS">
      <SponsoredCars showLargeArrows verticalOffset="50%" />
    </MinHeightWrapper>
  );

  const vehicleEditorialRankingsLength = vehicleEditorialRankings.length;

  return (
    <Fragment>
      {winnerFlag}
      <div className="vehicle-type-rankings-list">
        {vehicleEditorialRankings.map((subtype, index) => {
          const {
            id = '',
            displayName = '',
            description = '',
            edmundsTypeCategory = '',
            segmentRatings = [],
          } = subtype;
          const listId = getAnchorName(displayName);
          const subtypeRankedVehicleCount = segmentRatings.filter(({ rating }) => !!rating).length;
          const isAlwaysOpened = subtypeRankedVehicleCount < RANKING_DATA.SUBTYPES_TO_COLLAPSE || isSubtypePage;
          const usingPreloadAboveFold = TYPES_FOR_PRELOAD_ABOVE_FOLD.has(type);
          let isSingleCategory;

          if (!usingPreloadAboveFold) {
            isSingleCategory = vehicleEditorialRankingsLength === 1;
          } else {
            // When preloadAboveFold is being used, server side data comes bacgk with only one single category
            // However, we still want the "See All" button to render on server side to account for CLS impact
            isSingleCategory = false;
          }

          return (
            <Fragment key={`rankings-${id}`}>
              <ScrollElement id={listId} className="pos-r">
                <VehicleSubtypeRankingsList
                  type={type}
                  subtype={{ ...subtype, id, displayName, description, edmundsTypeCategory, segmentRatings }}
                  onSelectCar={onSelectCar}
                  selectedCars={selectedCars}
                  isAlwaysOpened={isAlwaysOpened}
                  dataTrackingParent={`edm-entry-subtype-${index + 1}`}
                  isLazySubtypesFFDisabled={isLazySubtypesFFDisabled}
                  isMobile={isMobile}
                  isSubtypePage={isSubtypePage}
                  isSubtypeExpansionPage={isSubtypeExpansionPage}
                  index={index}
                  trkParam={trkParam}
                  isSingleCategory={isSingleCategory}
                  subtypeAdPlacementIndices={subtypeAdPlacementIndices}
                  isReplaceImage={isReplaceImage && REPLACE_TYPES.includes(type)}
                />
                {index === 0 && children}
              </ScrollElement>
              {!isSubtypePage && (
                <Fragment>
                  {index === 1 && renderFilmstripTypeRankingAd()}
                  {index === 2 && renderFilmstripAdUnits}
                  {vehicleEditorialRankings.length >= 6 && index === 5 && (
                    <Experiment name="ads-10774" showDefault>
                      <Recipe name="ctrl" isDefault />
                      <Recipe name="chal">{renderFilmstripTypeRankingAd('1')}</Recipe>
                    </Experiment>
                  )}
                </Fragment>
              )}
            </Fragment>
          );
        })}
        {!!vehicleEditorialRankings.length && !isSubtypePage && (
          <Fragment>
            {vehicleEditorialRankings.length < 2 && renderFilmstripTypeRankingAd()}
            {vehicleEditorialRankings.length < 3 && renderFilmstripAdUnits}
          </Fragment>
        )}
      </div>
    </Fragment>
  );
}

VehicleTypeRankingsListUI.propTypes = {
  type: PropTypes.string.isRequired,
  onSelectCar: PropTypes.func.isRequired,
  selectedCars: TypeRankingsEntities.SelectedCars.isRequired,
  vehicleEditorialRankings: PropTypes.arrayOf(TypeRankingsEntities.VehicleRanking),
  isMobile: PropTypes.bool,
  children: PropTypes.node,
  trkParam: PropTypes.shape({
    trk: PropTypes.string,
  }),
  isLazySubtypesFFDisabled: PropTypes.bool,
  isSubtypePage: PropTypes.bool,
  isSubtypeExpansionPage: PropTypes.bool,
  subtypeAdPlacementIndices: PropTypes.arrayOf(PropTypes.shape({})),
  isReplaceImage: PropTypes.bool,
};

VehicleTypeRankingsListUI.defaultProps = {
  isMobile: false,
  vehicleEditorialRankings: [],
  children: null,
  trkParam: {},
  isLazySubtypesFFDisabled: false,
  isSubtypePage: false,
  isSubtypeExpansionPage: false,
  subtypeAdPlacementIndices: null,
  isReplaceImage: false,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  // FF is meant to "Disable" when enabled. Confusing, I know.
  isLazySubtypesFFDisabled: !!state.featureFlags.lazySubtypes,
  isReplaceImage: !!state.featureFlags.replaceImageTypePage,
});

export const VehicleTypeRankingsList = connect(mapStateToProps)(React.memo(VehicleTypeRankingsListUI));
VehicleTypeRankingsList.propTypes = {
  onSelectCar: PropTypes.func.isRequired,
  selectedCars: TypeRankingsEntities.SelectedCars.isRequired,
};
